import {showDirectorySuccess} from '@/helpers/directorySuccessAlert.js'

const warningIcon = `
    <svg width="56" height="56" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="24" cy="24" r="24" fill="#FF671F" fill-opacity="0.12"/>
        <path d="M24 20.4V24.4M14.1 28C13.2 29.6 14.4 31.6 16.2 31.6H31.8C33.6 31.6 34.8 29.6 33.9 28L26.1 14.8C25.2 13.2 22.8 13.2 21.9 14.8L14.1 28ZM24 27.6H24.01" stroke="#FF671F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
`

function formatDisplayName(value, fallback) {
    return String(value || fallback).toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase())
}

function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    }[char]))
}

function defaultDescription({ action, highlightedName, currentStatus, nextStatus, entityName }) {
    const entityLabel = entityName.toLowerCase()

    return `Are you sure you want to ${action.toLowerCase()} ${highlightedName}? This ${entityLabel} status will change from ${currentStatus} to ${nextStatus}.`
}

function defaultSuccessDescription({ highlightedName, nextStatus, entityName, completedAction }) {
    const entityLabel = entityName.toLowerCase()

    return `The ${entityLabel} ${highlightedName} has been ${completedAction.toLowerCase()} successfully. Its status is now ${nextStatus}.`
}

export function confirmDirectoryAction(vm, options) {
    const {
        title,
        description,
        confirmButtonText,
        onConfirm,
        successDescription,
        errorMessage = 'Unable to complete action.'
    } = options

    return vm.$swal({
        title,
        html: description,
        showCancelButton: true,
        icon: 'warning',
        iconHtml: warningIcon,
        confirmButtonText,
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
            popup: 'directory-status-swal',
            icon: 'directory-status-swal__icon',
            title: 'directory-status-swal__title',
            htmlContainer: 'directory-status-swal__text',
            actions: 'directory-status-swal__actions',
            confirmButton: 'directory-status-swal__confirm',
            cancelButton: 'directory-status-swal__cancel'
        },
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !vm.$swal.isLoading(),
        preConfirm: () => {
            return Promise.resolve(onConfirm()).then((res) => {
                if (res !== 'OK') {
                    vm.$swal.showValidationMessage(res || errorMessage)
                    return false
                }

                return res
            }).catch((error) => {
                vm.$swal.showValidationMessage(error?.message || errorMessage)
                return false
            })
        }
    }).then((res) => {
        if (res.isConfirmed && successDescription) {
            showDirectorySuccess(vm, {
                description: successDescription
            })
        }
    })
}

export function confirmDirectoryStatusChange(vm, options) {
    const {
        entityName,
        itemName,
        isActive,
        onConfirm,
        activeDescription,
        inactiveDescription,
        successDescription,
        successDescriptionBuilder,
        errorMessage = 'Unable to update status.'
    } = options

    const action = isActive ? 'Deactivate' : 'Activate'
    const completedAction = isActive ? 'Deactivated' : 'Activated'
    const currentStatus = isActive ? 'Active' : 'Inactive'
    const nextStatus = isActive ? 'Inactive' : 'Active'
    const displayName = escapeHtml(formatDisplayName(itemName, `this ${entityName.toLowerCase()}`))
    const highlightedName = `<strong class="directory-status-swal__name">${displayName}</strong>`
    const descriptionBuilder = isActive ? activeDescription : inactiveDescription
    const description = descriptionBuilder
        ? descriptionBuilder({ action, highlightedName, currentStatus, nextStatus, entityName })
        : defaultDescription({ action, highlightedName, currentStatus, nextStatus, entityName })

    return vm.$swal({
        title: `${action} ${entityName}?`,
        html: description,
        showCancelButton: true,
        icon: 'warning',
        iconHtml: warningIcon,
        confirmButtonText: action,
        cancelButtonText: 'Cancel',
        buttonsStyling: false,
        customClass: {
            popup: 'directory-status-swal',
            icon: 'directory-status-swal__icon',
            title: 'directory-status-swal__title',
            htmlContainer: 'directory-status-swal__text',
            actions: 'directory-status-swal__actions',
            confirmButton: 'directory-status-swal__confirm',
            cancelButton: 'directory-status-swal__cancel'
        },
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !vm.$swal.isLoading(),
        preConfirm: () => {
            return Promise.resolve(onConfirm()).then((res) => {
                if (res !== 'OK') {
                    vm.$swal.showValidationMessage(res || errorMessage)
                    return false
                }

                return res
            }).catch((error) => {
                vm.$swal.showValidationMessage(error?.message || errorMessage)
                return false
            })
        }
    }).then((res) => {
        if (res.isConfirmed) {
            const moduleSuccessDescription = successDescriptionBuilder
                ? successDescriptionBuilder({ action, completedAction, highlightedName, currentStatus, nextStatus, entityName })
                : successDescription || defaultSuccessDescription({ completedAction, highlightedName, nextStatus, entityName })

            showDirectorySuccess(vm, {
                description: moduleSuccessDescription
            })
        }
    })
}
